import {
  useDeleteTemplate,
  useQuerySendEmail,
  useQuerySendEmailSequence,
  useRemoveEmailFromSequence,
} from "./queries";
import { handleEmailGenerationError } from "@/hooks/use-email-templates-manager";
import { EmailFormFormattedDataI } from "./interfaces";
import { useEmailTemplatesContext } from "./context";
import { useEffect, useMemo, useRef } from "react";
import {
  AccountDetailsContextI,
  useAccountDetailsContext,
} from "@/modules/pipeline/account-details/context";
import {
  SendEmailRequestParamsI,
  SendEmailSequenceRequestParamsI,
} from "@/api/routes/email";
import { selectorEmailsSequenceDaysDiff } from "./selectors";
import { DayJs } from "shared/lib/helpers/date";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { EMAIL_TEMPLATE_DELETE_MODAL_ID } from "@/components/modals/email-template-delete-modal";
import { isEmailHTMLBodyEmpty } from "shared/lib/helpers/email";
import toast from "react-hot-toast";

export const useEmailSender = (onSuccess?: () => void) => {
  const { drafts, activeTemplateId, emailGenerationConfig, getDraft } =
    useEmailTemplatesContext();

  const accountDetailsContext = useAccountDetailsContext();
  const AccountDetailsRef = useRef<AccountDetailsContextI>(
    accountDetailsContext
  );
  useEffect(() => {
    AccountDetailsRef.current = accountDetailsContext;
  }, [accountDetailsContext]);

  /**
   * Every drafts update we need to make sure we have latest current draft
   */
  const draft = useMemo(
    () => getDraft(activeTemplateId as string),
    [drafts, activeTemplateId]
  );

  const { mutateAsync: sendEmailAsync, isPending: isSendEmailPending } =
    useQuerySendEmail();

  const {
    mutateAsync: sendEmailSequenceAsync,
    isPending: isSendEmailSequencePending,
  } = useQuerySendEmailSequence();

  const sendSingleEmail = async (formData: EmailFormFormattedDataI) => {
    const { focusedContact, campaign, account } = AccountDetailsRef.current;

    const parameters: SendEmailRequestParamsI = {
      ...formData,
      contact_id: focusedContact?.id as string,
      account_id: account?.id as string,
      campaign_id: campaign?.id as string,
    };

    const resp = await sendEmailAsync({
      parameters,
    });

    if (resp.status !== 200) {
      handleEmailGenerationError(resp, "Failed to send email");
      return;
    }

    onSuccess?.();
  };

  const sendEmailSequence = async ({
    to,
    cc,
    bcc,
  }: EmailFormFormattedDataI) => {
    const draftEmails = draft?.emails;

    const parameters: SendEmailSequenceRequestParamsI = {
      emails: draftEmails?.map((email, i) => ({
        email_id: email?.email_id,
        from: emailGenerationConfig?.from_email as string,
        to,
        cc,
        bcc,
        subject: email?.subject as string,
        html_body: email?.body as string,
        // plain_body?: string;
        attachments:
          i === 0
            ? draft?.attachments?.map((attachment) => attachment?.id)
            : undefined,
        scheduled_at: email?.scheduled_at,
      })),
    };

    if (
      parameters.emails?.find((email) => isEmailHTMLBodyEmpty(email?.html_body))
    ) {
      toast.error("Email body is required for each email in the sequence");
      return;
    }

    const resp = await sendEmailSequenceAsync({
      sequenceId: draft?.sequence_id as string,
      parameters,
    });

    if (resp.status !== 200) {
      handleEmailGenerationError(resp, "Failed to send email");
      return;
    }

    onSuccess?.();
  };

  const emailHandler = async (data: EmailFormFormattedDataI) => {
    if (draft?.sequence_id) {
      await sendEmailSequence(data);
    } else {
      await sendSingleEmail(data);
    }
  };

  return {
    isPending: isSendEmailPending || isSendEmailSequencePending,
    handler: emailHandler,
  };
};

/**
 * Remove the followup email from the sequence
 * and set the first email as active
 * and recalculate days for each email in the sequence
 */
export const useHandlerRemoveEmailFromSequence = () => {
  const { getDraft, setDraft, activeTemplateId, setActiveEmailSequenceItemId } =
    useEmailTemplatesContext();

  const {
    mutateAsync: removeEmailFromSequenceAsync,
    isPending: isRemovingEmailFromSequence,
  } = useRemoveEmailFromSequence();

  /**
   * Remove the followup email from the sequence
   * and set the first email as active
   * and recalculate days for each email in the sequence
   */
  const handler = async (emailId: string) => {
    const draft = getDraft(activeTemplateId as string) || {};
    const emails = draft?.emails;
    const emailIndex =
      emails?.findIndex((email) => email?.email_id === emailId) || 0;

    const emailsDaysDiff = selectorEmailsSequenceDaysDiff(emails).filter(
      (_, i) => i !== emailIndex
    );

    const filteredEmails = draft?.emails?.filter((_, i) => i !== emailIndex);

    const resp = await removeEmailFromSequenceAsync(emailId);

    if (resp?.status !== 200) {
      return;
    }

    const updatedEmails = filteredEmails?.reduce(
      (emails, email, i) => {
        const prevDate = i > 1 ? emails?.[i - 1]?.scheduled_at : undefined;

        return [
          ...emails,
          {
            ...email,
            scheduled_at: DayJs(prevDate)
              .add(emailsDaysDiff?.[i] || 0, "days")
              .format(),
          },
        ];
      },
      [] as typeof filteredEmails
    );

    setActiveEmailSequenceItemId(draft?.emails?.[0]?.email_id);

    setDraft(activeTemplateId as string, {
      ...draft,
      emails: updatedEmails,
    });
  };

  return { handler, isPending: isRemovingEmailFromSequence };
};

export const useHandlerDeletePrompt = () => {
  const {
    activeTemplate,
    setEditMode,
    templates: templatesData,
    setActiveTemplateId,
  } = useEmailTemplatesContext();
  const { mutateAsync: deleteTemplate, isPending } = useDeleteTemplate();

  const openModal = () => {
    modalHelpers.open(EMAIL_TEMPLATE_DELETE_MODAL_ID);
  };

  const handlerOnConfirm = (_templateId?: string) => async () => {
    const templateId = _templateId || activeTemplate?.id;

    await deleteTemplate(templateId as string);

    const templates = templatesData?.data?.email_templates;

    const template = templates?.filter((t) => !t?.is_custom)[0];

    setEditMode(false);
    setActiveTemplateId(template?.id as string);
  };

  return {
    openModal,
    handlerOnConfirm,
    isPending,
  };
};
